import { memo, useContext, useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Box, Checkbox, FieldSet, Label, LabeledControl, Legend, Radio } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import { debounce } from '@zg-rentals/zdash';

import PropertiesActions from '../../../../actions/PropertiesActions';
import wizardActions from '../../../../components/Wizard/actions';
import actions from '../../../../containers/CreateListingPage/actions';
import { useForm, useGuardedMultiEdit } from '../../../../hooks';
import {
  MULTIPLEX_AMENITY_KEYS,
  useMultiplexBuildingSessionId,
  useMultiplexDetails,
  useMultiplexListingFlow,
  useMultiplexUpdate,
} from '../../../../hooks/queries/multiplex';
import { useSelector } from '../../../../selectors/useSelector';
import { getDataToSave } from '../../../../utils/amenitiesUtils';
import { postingConstraints } from '../../../../utils/formValidation';
import { multiFieldsUtil } from '../../../../utils/postingUtils';

import TimeConstants from '../../../../constants/TimeConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import Amenities from '../../../../model/Amenities';
import WizardServerError from '../../../../model/WizardServerError';

import DisabledAccessAmenityTooltip from '../../../../components/DisabledAccessAmenityTooltip';
import { WizardContext } from '../../../../components/Wizard/context';
import WizardAlert from '../../../../components/WizardAlert';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import type { ChangeEvent } from 'react';
import type {
  MultiplexBuildingSessionAmenityGroupWebDto,
  UserPropertyMultiEditRequestWebDto,
  UserPropertyMultiplexUpdateRequestWebDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { AmenitiesProps } from '../../../../model/Amenities';

/**
 * Chooses which amenity list drives the form:
 * - Standard flow: Redux `amenities.property` only.
 * - Multiplex flow: `useMultiplexDetails().data.amenities.property` when present (order + labels + selections from session);
 *   merge `isEditable` / `isRequired` / `isUniqueSelection` from Redux by matching `category`.
 * - Multiplex flow before details load: keep Redux structure but clear selections so Redux `isSelected` is not shown.
 */
const buildAmenityList = ({
  isMultiplexFlowActive,
  multiplexPropertyGroups,
  amenitiesPropertyList,
}: {
  isMultiplexFlowActive: boolean;
  multiplexPropertyGroups: Array<MultiplexBuildingSessionAmenityGroupWebDto> | undefined;
  amenitiesPropertyList: AmenitiesProps['property'];
}): AmenitiesProps['property'] => {
  if (!isMultiplexFlowActive) {
    return amenitiesPropertyList;
  }

  if (!multiplexPropertyGroups?.length) {
    return amenitiesPropertyList.map((group) => ({
      ...group,
      values: group.values.map((value) => ({ ...value, isSelected: false })),
    }));
  }

  return multiplexPropertyGroups.map((muxGroup) => {
    const matchedGroup = amenitiesPropertyList.find((g) => g.category === muxGroup.category);

    return {
      category: muxGroup.category,
      isEditable: matchedGroup?.isEditable ?? true,
      isRequired: matchedGroup?.isRequired ?? false,
      isUniqueSelection: matchedGroup?.isUniqueSelection ?? false,
      values: muxGroup.values.map((v) => ({
        displayName: v.displayName,
        fieldName: v.fieldName,
        isSelected: v.isSelected,
      })),
    };
  });
};

/**
 * Maps flat multi-edit `property` booleans onto multiplex PUT fields. Drops unrelated keys (`listingAlias`, interior
 * amenities, etc.): bulk multiplex update only applies supplied keys; omitting keys leaves them unchanged server-side.
 */
const pickMultiplexAmenityPostData = (
  propertyRecord: Record<string, unknown>,
): Partial<UserPropertyMultiplexUpdateRequestWebDto> => {
  const out: Partial<UserPropertyMultiplexUpdateRequestWebDto> = {};

  for (const key of MULTIPLEX_AMENITY_KEYS) {
    const value = propertyRecord[key];
    if (typeof value === 'boolean') {
      out[key] = value;
    }
  }

  return out;
};

const saveDebouncedViaMultiEndpoint = debounce(
  (saveViaMultiEndpoint: () => void) => saveViaMultiEndpoint(),
  TimeConstants.SELECTION_DEBOUNCE_TIME_MS,
);

const AmenitiesPropertyFormField = ({ isRelistingFieldset }: { isRelistingFieldset?: boolean }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { data } = useMultiplexDetails();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  /** Coerce `boolean | null` to boolean (`null` while feature gate loads → treated as not in multiplex). */
  const isMultiplexFlowActive = Boolean(isInMultiplexListingFlow);
  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();
  const { mutateAsync: multiplexUpdateAsync } = useMultiplexUpdate({
    meta: {
      context: {
        api: 'useMultiplexUpdate',
        details: 'AmenitiesPropertyFormField',
        fieldName: 'amenities',
      },
    },
  });

  const { amenitiesPropertyList, listingAlias } = useSelector(
    (state) => ({
      amenitiesPropertyList: state.properties.currentProperty.amenities.property,
      listingAlias: state.properties.currentProperty.listingAlias,
    }),
    shallowEqual,
  );

  const amenityList = useMemo(
    () =>
      buildAmenityList({
        isMultiplexFlowActive,
        multiplexPropertyGroups: data?.amenities?.property,
        amenitiesPropertyList,
      }),
    [amenitiesPropertyList, data?.amenities?.property, isMultiplexFlowActive],
  );

  /**
   * Same amenity list as render: non-multiplex uses Redux `amenities.property`; multiplex merges session details when active.
   */
  const { initialInputs, categorizedAmenitiesKeys } = useMemo(() => {
    const categorizedAmenitiesKeys: Record<string, Array<string>> = { checkboxes: [] as Array<string> };

    const initialInputs: Record<string, boolean | string> = amenityList.reduce(
      (a: Record<string, boolean | string>, { category, isUniqueSelection, values }: AmenitiesProps['property'][0]) => {
        if (isUniqueSelection) {
          categorizedAmenitiesKeys[category] = [];
        }

        values.forEach(({ fieldName, isSelected }) => {
          const selected = Boolean(isSelected);
          if (!isUniqueSelection) {
            a[fieldName] = selected;
          } else if (selected) {
            a[category] = fieldName;
          }

          if (isUniqueSelection) {
            categorizedAmenitiesKeys[category].push(fieldName);
          } else {
            categorizedAmenitiesKeys.checkboxes.push(fieldName);
          }
        });

        return a;
      },
      {},
    );
    return { initialInputs, categorizedAmenitiesKeys };
  }, [amenityList]);

  const { inputs, hasFieldError, handleChange: handleFormChange } = useForm(initialInputs, postingConstraints);

  const amenityFieldInputs = inputs as Record<string, boolean | string>;

  const { saveMultipleFieldsPropertyInfo } = useGuardedMultiEdit();

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const saveViaMultiEndpoint = ({ dataToSave }: { dataToSave: UserPropertyMultiEditRequestWebDto }) => {
    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    queryClient.invalidateQueries({ queryKey: ['getListingRecommendations', listingAlias] });

    return saveMultipleFieldsPropertyInfo({ postData: dataToSave })
      .then((response) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          updateServerError(errorMessage, 'multi');
          return undefined;
        }
        const {
          // @ts-expect-error Multi-edit response typing does not narrow `listingOutputFieldData`; runtime guard above handles failures.
          listingOutputFieldData: { amenities },
        } = response;
        dispatch(PropertiesActions.updateCurrentProperty({ amenities: Amenities(amenities) }));

        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

        return undefined;
      })
      .catch((err: unknown) => {
        updateServerError((err as { message?: string })?.message ?? '', 'multi');
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const saveMultiplex = async ({ dataToSave }: { dataToSave: UserPropertyMultiEditRequestWebDto }) => {
    /**
     * Defensive guard only: multiplex save runs when `isMultiplexFlowActive`, which should already imply a session id.
     * `useMultiplexUpdate` also rejects empty ids before any request.
     */
    if (!multiplexBuildingSessionId) {
      logError({
        error: new Error('Unable to save: missing multiplexBuildingSessionId'),
        context: {
          details: 'AmenitiesPropertyFormField.saveMultiplex',
        },
      });

      return;
    }

    const propertyRecord = dataToSave.property as unknown as Record<string, unknown>;
    const postData = pickMultiplexAmenityPostData(propertyRecord);

    // After dropping unrelated keys, nothing left to send (e.g. only interior-only fields changed).
    if (Object.keys(postData).length === 0) {
      return;
    }

    try {
      const response = await multiplexUpdateAsync({
        getParams: { multiplexBuildingSessionId },
        postData,
      });

      const flatErrors = response.units?.flatMap((unit) => unit.errors ?? []) ?? [];
      const hasUnitFailure = response.units?.some((unit) => unit.success === false) ?? false;

      if (flatErrors.length > 0 || hasUnitFailure || response.success === false) {
        const amenityKeyError = flatErrors.find((err) => MULTIPLEX_AMENITY_KEYS.some((key) => key === err.fieldName));

        updateServerError(amenityKeyError?.message ?? flatErrors[0]?.message ?? 'Unable to save amenities.', 'multi');
      }
    } catch (err: unknown) {
      const message = DatarouterError.is(err)
        ? DatarouterError.getDisplayMessage(err)
        : ((err as { message?: string })?.message ?? '');

      updateServerError(message, 'multi');
    }
  };

  useEffect(() => {
    if (isSaveComplete) {
      const dataToSave = getDataToSave({
        inputs: amenityFieldInputs,
        categorizedAmenitiesKeys,
        listingAlias,
      });

      if (isMultiplexFlowActive) {
        saveMultiplex({ dataToSave });
      } else {
        saveViaMultiEndpoint({ dataToSave });
      }
    }
    // Intentionally depend only on `isSaveComplete` so this fires when the CTA completes, not on every form edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync save only when CTA marks complete
  }, [isSaveComplete]);

  const save = (event: ChangeEvent<HTMLInputElement>) => {
    const { name: fieldName, value } = event.target;

    const valueToSave = categorizedAmenitiesKeys[fieldName] ? value : value === 'true';

    clearServerError();
    handleFormChange({ target: { name: fieldName, value: valueToSave } });

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    if (hasFieldError('multi', amenityFieldInputs)) {
      return;
    }

    saveDebouncedViaMultiEndpoint(() => {
      const dataToSave = getDataToSave({
        inputs: amenityFieldInputs,
        categorizedAmenitiesKeys,
        listingAlias,
        fieldName,
        valueToSave,
      });

      if (isMultiplexFlowActive) {
        saveMultiplex({ dataToSave });
      } else {
        saveViaMultiEndpoint({ dataToSave });
      }
    });
  };

  return (
    <div data-test="AmenitiesPropertyFormField">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <Box
        data-testid="AmenitiesPropertyFormFieldContent"
        css={{ display: 'flex', flexDirection: 'row', gap: '400', flexWrap: 'wrap' }}
      >
        {amenityList.map(
          ({ category, isEditable, isRequired, isUniqueSelection, values }: AmenitiesProps['property'][0]) => (
            <Box
              key={`${category}FieldSetContainer`}
              css={{ display: 'flex', flexDirection: 'column', gap: '400', flex: '1 0 280px' }}
            >
              <FieldSet
                disabled={!isEditable}
                required={isRequired && isEditable}
                legend={<Legend size={isRelistingFieldset ? 'lg' : 'md'}>{category}</Legend>}
              >
                {values.map(({ displayName, fieldName }) => {
                  const ControlInput = isUniqueSelection ? Radio : Checkbox;
                  const label =
                    fieldName === 'hasDisabledAccess' ? (
                      <>
                        <Label>{displayName}</Label>
                        <DisabledAccessAmenityTooltip />
                      </>
                    ) : (
                      <Label>{displayName}</Label>
                    );

                  return (
                    <LabeledControl
                      key={displayName}
                      label={label}
                      control={
                        <ControlInput
                          name={isUniqueSelection ? category : fieldName}
                          value={isUniqueSelection ? fieldName : String(!inputs[fieldName])}
                          checked={isUniqueSelection ? inputs[category] === fieldName : Boolean(inputs[fieldName])}
                          onChange={save}
                        />
                      }
                    />
                  );
                })}
              </FieldSet>
            </Box>
          ),
        )}
      </Box>
    </div>
  );
};

export default memo(AmenitiesPropertyFormField);
